import React, { useCallback, useEffect } from 'react';
import cls from 'classnames';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useBoolean, useEffectOnce } from 'react-use';
import MessageContainer from '@/components/message/items/message-container';
import MessageAvatar from '@/components/message/items/message-avatar';
import audio from '@/utils/audio';
import {
  GET_AUDIO_SENDING_STATUS,
  START_SEND_AUDIO,
  SEND_MESSAGE,
  bus,
} from '@/event/message_events';
import Toast from '@/components/toast/toast';
import styles from './audio-message.module.scss';
import { CDN_BASE_URL } from '@/constants';

const AudioIcon = (props) => {
  const { playing, onClick, fromMe } = props;
  const position = fromMe ? 'right' : 'left';
  const helperNum = 4;
  return (
    <div className={cls(styles.audioIcon, playing && styles.playing, styles[position])} onClick={onClick}>
      {[1, 2, 3].map((i) => {
        const iconIndex = fromMe ? helperNum - i : i;
        return (
          <img
            key={`audio-status-${i}`}
            className={cls(styles[position], styles[`audio-icon-segment-${i}`])}
            src={`${CDN_BASE_URL}/audio-icon-${position}-${iconIndex}.svg`}
            alt={`${i}`}
            data-no-preview="true"
          />
        );
      })}
    </div>
  );
};

AudioIcon.propTypes = {
  onClick: PropTypes.func,
  playing: PropTypes.bool,
  fromMe: PropTypes.bool,
};

const AudioMessage = (props) => {
  const {
    avatar,
    senderOid,
    senderType,
    fromMe,
    avatarClassName,
    duration,
    audioUri,
    error,
    msgContent,
  } = props;
  const [playState, togglePlayState] = useBoolean(false);
  const formattedDuration = Math.ceil(duration);

  useEffectOnce(() => {
    bus.on(START_SEND_AUDIO, () => {
      audio.stop();
    });
  });

  const sendMsg = useCallback(() => {
    bus.emit(SEND_MESSAGE, msgContent);
  }, [msgContent]);

  const handleStop = useCallback(() => {
    togglePlayState(false);
  }, [togglePlayState]);

  useEffect(() => {
    audio.registerStopListener(audioUri, handleStop);
    return () => {
      audio.offStop(audioUri);
    };
  }, [audioUri, handleStop]);

  const handleAudioClick = useCallback(
    (e) => {
      e.stopPropagation();
      bus.emit(GET_AUDIO_SENDING_STATUS, (status) => {
        if (status) {
          const containerBlock = document.createElement('div');
          ReactDOM.render(
            <Toast content="录音过程中不能播放语音" />,
            containerBlock
          );
          document.body.appendChild(containerBlock);

          setTimeout(() => {
            ReactDOM.unmountComponentAtNode(containerBlock);
            document.body.removeChild(containerBlock);
          }, 3000);
        } else if (playState) {
          audio.stop();
        } else {
          audio.play(audioUri);
          togglePlayState(true);
        }
      });
    },
    [audioUri, togglePlayState, playState]
  );

  return (
    <MessageContainer fromMe={fromMe}>
      <MessageAvatar
        className={avatarClassName}
        fromMe={fromMe}
        avatar={avatar}
        senderOid={senderOid}
        senderType={senderType}
      />
      <div
        className={cls(styles.audioContent, 'ms-content ms-audio-content', { right: fromMe, left: !fromMe })}
        style={{ width: 94 + (formattedDuration - 1) * 6 }}
        onClick={handleAudioClick}
      >
        <AudioIcon playing={playState} fromMe={fromMe} />
        <span className={styles.audioTime}>{`${formattedDuration}s`}</span>
      </div>
      {error && (
        <a className={styles.retry} onClick={sendMsg}>
          重发
        </a>
      )}
    </MessageContainer>
  );
};

AudioMessage.propTypes = {
  avatar: PropTypes.string,
  senderType: PropTypes.string,
  senderOid: PropTypes.string,
  fromMe: PropTypes.bool,
  avatarClassName: PropTypes.string,
  duration: PropTypes.number,
  audioUri: PropTypes.string,
  error: PropTypes.bool,
  msgContent: PropTypes.object,
};

export default AudioMessage;
